import { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { isEqual } from "lodash";

// hooks
import useDebouncedSearch from "hooks/useDebouncedSearch";

// store
import useFiltersStore from "store/useFiltersStoreNew";

// utils
import { sendAdvancedSearchEvent } from "utils/gtm";

export default function useResultQueryV2() {
  const { filters, areFiltersApplied, lastSentFilters, setLastSentFilters } =
    useFiltersStore((state) => ({
      filters: state.filters,
      areFiltersApplied: state.areFiltersApplied,
      lastSentFilters: state.lastSentFilters,
      setLastSentFilters: state.setLastSentFilters,
    }));

  const [debouncedFilters, isDebouncing] = useDebouncedSearch(filters, 750);

  const isQueryEnabled = useMemo(() => {
    return (
      areFiltersApplied() && 
      !isDebouncing && 
      isEqual(filters, debouncedFilters)
    ) 
  }, [filters, debouncedFilters, isDebouncing, areFiltersApplied]);

  const {
    isLoading,
    isError,
    data,
    dataUpdatedAt
  } = useQuery({
    queryKey: ["results", JSON.stringify(filters)],
    queryFn: () => results(debouncedFilters),
    enabled: isQueryEnabled,
    staleTime: 1000 * 60 * 30, // refresh every half hour
    retry: false,
    refetchOnWindowFocus: false,
  });

  const isQueryLoading = dataUpdatedAt === 0 ? isLoading || isDebouncing : false // dataUpdatedAt will be 0 if there is no cached data

  useEffect(() => {
    // Only send event if filters are applied and debouncedFilters differs from lastSentFilters
    if (areFiltersApplied() && !isEqual(debouncedFilters, lastSentFilters)) {
      sendAdvancedSearchEvent(debouncedFilters);
      setLastSentFilters(debouncedFilters); // Update lastSentFilters after sending
    }
  }, [debouncedFilters, areFiltersApplied]);

  return {
    data,
    isLoading: isQueryLoading,
    isError,
    dataUpdatedAt
  }
}

function results(filters) {

  const fetchData = async () => {

    try {
      const response = await fetch(`/_api/v2/search`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(filters),
          // signal, // Pass the signal to the fetch request to allow for aborting
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch results data");
      }

      const data = await response.json();
      return data

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted:', error);
      } else {
        console.error("Error fetching meili results: ", error);
      }
    }
  };

  return fetchData()
}